import { notification } from "@excent-one/react/notification";
import { BadRequestError } from "@excent-one/core";
import { capitalCase } from "text-case";
import {
  ControlledDataGridColumnProps,
  FormFor,
  ControlledDataGrid,
  ControlledInput,
  ControlledDatePicker,
  ControlledComboBox,
  FormSection,
} from "@excent-one/react/form";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UseFormSetValue, useWatch } from "react-hook-form";
import { Button, FormFieldSet } from "@progress/kendo-react-all";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderBackNavigation,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
} from "@excent-one/react/layout/content";
import { dialogBox } from "@excent-one/react/dialog-box";
import { saveIcon, cancelIcon, pencilIcon } from "@progress/kendo-svg-icons";
import { useRouteHandle } from "@excent-one/react/routing/useRouteHandle";
import FormActionsBar from "@excent-one/react/form/layouts/FormActionsBar";
import { useAuthContext } from "@excent-one/auth";
import { resolveTransmittalAttachmentUrl } from "@phrmpc/shared/hooks/useTransmittalAttachmentContent";
import {
  cancelTransmittalRecord,
  createTransmittalDocument,
  deleteTransmittalDocument,
  deleteTransmittalDocumentFile,
  getCoordinators,
  getMyDetails,
  getTransmittalDocumentFiles,
  getTransmittalRecordById,
  receiveTransmittalRecord,
  releaseTransmittalRecord,
  saveTransmittalRecord,
  TransmittalDocumentDto,
  TransmittalRecordDto,
  TransmittalRecordInputDto,
  uploadFileTransmittalDocuments,
} from "@phrmpc/infrastructure/phrmpc";
import {
  type MemberBasicInfoDto,
  type TransmittalDocumentFileInfoDto,
} from "@phrmpc/infrastructure/phrmpc/models";

const TRANSMITTAL_ATTACHMENT_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
];
// Matches the removed ControlledUpload's own DEFAULT_MAX_FILE_SIZE - this call site never passed
// an explicit maxFileSize before, so it relied on that 20MB default; the library's own restriction
// has no default (an omitted maxFileSize means unbounded), so it must be set explicitly here to
// avoid silently lifting the size cap.
const TRANSMITTAL_ATTACHMENT_MAX_FILE_SIZE = 20 * 1024 * 1024;

const STATUS_OPTIONS = [
  { label: "Draft", value: "Draft" },
  { label: "Released", value: "Released" },
  { label: "Received", value: "Received" },
  { label: "Cancelled", value: "Cancelled" },
];

interface TransmittalDocumentRow extends Omit<TransmittalDocumentDto, "id"> {
  id: string;
  // Local-only, single-file display fields the "upload" grid columns actually bind to (with
  // `multiple: false`) - the library's own multi-file `onUpload` append logic always merges its
  // return value into whatever the array-typed `filesReleased`/`filesReceived` DTO fields held at
  // the moment it runs, which can't be reliably cleared from outside in time to get replace (not
  // accumulate) semantics (confirmed live via a Playwright test: an external setValue clearing the
  // array first, or a value-comparison resync effect correcting it after, both still lost the race
  // to the library's own append). `multiple: false` sidesteps this: its replace logic (`[t]`,
  // unconditionally, ignoring prior state) is used unconditionally instead. `filesReleased`/
  // `filesReceived` remain the source of truth for the save payload, kept in sync from server data
  // by the resync effect below - this field is purely what the grid cell displays/edits.
  attachment: TransmittalDocumentFileInfoDto | null;
  receiveAttachment: TransmittalDocumentFileInfoDto | null;
}

const defaultDocumentRow: TransmittalDocumentRow = {
  id: "",
  documentName: "",
  quantityReleased: undefined,
  remarkReleased: "",
  quantityReceived: undefined,
  remarkReceived: "",
  filesReleased: [],
  filesReceived: [],
  attachment: null,
  receiveAttachment: null,
};

interface TransmittalFormValues extends Omit<
  TransmittalRecordDto,
  | "id"
  | "issuerId"
  | "issuerName"
  | "statusId"
  | "statusName"
  | "statusCode"
  | "date"
  | "releasedDate"
  | "receivedDate"
  | "transmittalDocuments"
> {
  date: Date;
  issuer: string;
  status: { label: string; value: string } | null;
  releasedDate: Date | null;
  receivedDate: Date | null;
  transmittalDocuments: TransmittalDocumentRow[];
}

const today = new Date();

const defaultValues: TransmittalFormValues = {
  transmittalId: "",
  date: today,
  issuer: "",
  receiverId: undefined,
  status: STATUS_OPTIONS[0],
  releasedDate: null,
  receivedDate: null,
  transmittalDocuments: [],
};

const mapRecordToFormValues = (
  record: TransmittalRecordDto,
  coordinators: MemberBasicInfoDto[],
  currentUserDisplayName?: string,
): TransmittalFormValues => ({
  transmittalId: record.transmittalId ?? "",
  date: record.date ? new Date(record.date) : today,
  issuer: record.issuerName || currentUserDisplayName || "",
  receiverId: record.receiverId ?? undefined,
  status:
    STATUS_OPTIONS.find((s) => s.value === record.statusName) ??
    STATUS_OPTIONS[0],
  releasedDate: record.releasedDate ? new Date(record.releasedDate) : null,
  receivedDate: record.receivedDate ? new Date(record.receivedDate) : null,
  transmittalDocuments: (record.transmittalDocuments ?? []).map(
    (doc: TransmittalDocumentDto) => ({
      id: doc.id!,
      documentName: doc.documentName ?? "",
      quantityReleased: doc.quantityReleased ?? null,
      remarkReleased: doc.remarkReleased ?? "",
      quantityReceived: doc.quantityReceived ?? null,
      remarkReceived: doc.remarkReceived ?? "",
      filesReleased: doc.filesReleased ?? [],
      filesReceived: doc.filesReceived ?? [],
      attachment: doc.filesReleased?.[0] ?? null,
      receiveAttachment: doc.filesReceived?.[0] ?? null,
    }),
  ) as TransmittalDocumentRow[],
});

const { Form, useFormRef } = FormFor<TransmittalFormValues>();

const buildInputPayload = (
  values: TransmittalFormValues,
  record: TransmittalRecordDto | undefined,
  coordinators: MemberBasicInfoDto[],
): TransmittalRecordInputDto => {
  const selectedReceiver = coordinators.find((c) => c.id === values.receiverId);
  return {
    statusId: record?.statusId,
    date: record?.date,
    receiverId: values.receiverId ?? record?.receiverId,
    receiverName: selectedReceiver?.fieldName ?? record?.receiverName,
    transmittalDocuments: values.transmittalDocuments.map((doc) => ({
      id: doc.id,
      documentName: doc.documentName,
      quantityReleased: doc.quantityReleased ?? undefined,
      remarkReleased: doc.remarkReleased,
      // Derived from the reliably-synced scalar `attachment`/`receiveAttachment` fields (see
      // TransmittalDocumentRow's own comment) rather than read from `filesReleased`/
      // `filesReceived` directly - those array fields depend on an invalidate-then-refetch resync
      // effect that, unlike the scalar fields' library-native replace, was confirmed live (via a
      // Playwright test) to not reliably re-render this grid's rows in time.
      filesReleased: doc.attachment ? [doc.attachment] : [],
      quantityReceived: doc.quantityReceived ?? undefined,
      remarkReceived: doc.remarkReceived,
      filesReceived: doc.receiveAttachment ? [doc.receiveAttachment] : [],
    })),
  };
};

const getServerErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof BadRequestError) {
    const errors = e.data?.errors as Record<string, string[]> | undefined;
    const messages = errors ? Object.values(errors).flat() : [];
    if (messages.length > 0) return messages.join(" ");
    if (e.data?.title) return e.data.title;
  }
  return fallback;
};

/** Shared `editorProps` builder for the release/receive "upload" grid columns. Binds to the row's
 *  local-only scalar `attachment`/`receiveAttachment` field (see `TransmittalDocumentRow`) with
 *  `multiple: false`, not directly to the array-typed `filesReleased`/`filesReceived` DTO fields -
 *  the library's own single-file replace logic (unconditionally committing the just-uploaded DTO,
 *  ignoring any prior value) only applies in `multiple: false` mode, and is the only way to get
 *  correct replace-on-reupload semantics here (confirmed live via a Playwright test: with
 *  `multiple: true`, the library always merges `onUpload`'s return value into whatever the bound
 *  field held when its post-upload `onFilesChange` fires, and no external `setValue` - immediate,
 *  or via an invalidate-then-refetch resync effect - reliably beat that merge to the punch; the
 *  array DTO fields needed for the save payload are instead derived directly from this scalar
 *  field in `buildInputPayload`, not independently synced). */
const buildTransmittalUploadEditorProps = ({
  transmittalDocumentId,
  readOnly,
}: {
  transmittalDocumentId: string;
  readOnly: boolean;
}) => ({
  multiple: false,
  permissionMode:
    readOnly || !transmittalDocumentId
      ? ("read-only" as const)
      : ("upload-delete" as const),
  restrictions: {
    allowedExtensions: TRANSMITTAL_ATTACHMENT_ALLOWED_EXTENSIONS,
    maxFileSize: TRANSMITTAL_ATTACHMENT_MAX_FILE_SIZE,
  },
  getFileName: (file: TransmittalDocumentFileInfoDto) => file.fileName ?? "",
  getFileUrl: (file: TransmittalDocumentFileInfoDto) =>
    file.id
      ? resolveTransmittalAttachmentUrl(
          transmittalDocumentId,
          file.id,
          file.fileName ?? "",
        )
      : undefined,
  onUpload: async (rawFile: File) => {
    if (!transmittalDocumentId) {
      notification.error("Save this row before attaching a file.");
      throw new Error("Missing transmittalDocumentId");
    }
    // This field has always been single-file/replace-on-reupload - delete any existing file(s) on
    // the server before uploading the new one so no orphaned attachment is left behind there
    // (the `multiple: false` binding above already ensures the *displayed* field replaces rather
    // than accumulates).
    const existingFiles =
      (await getTransmittalDocumentFiles(transmittalDocumentId)).items ?? [];
    await Promise.all(
      existingFiles
        .filter((f) => f.id)
        .map((f) =>
          deleteTransmittalDocumentFile(transmittalDocumentId, f.id!),
        ),
    );
    await uploadFileTransmittalDocuments(transmittalDocumentId, {
      file: rawFile,
    });
    const updatedFiles = await getTransmittalDocumentFiles(
      transmittalDocumentId,
    );
    const items = updatedFiles.items ?? [];
    return items[items.length - 1] ?? { fileName: rawFile.name };
  },
  onDelete: async (file: TransmittalDocumentFileInfoDto) => {
    if (!file.id) return;
    await deleteTransmittalDocumentFile(transmittalDocumentId, file.id);
  },
});

type SubmitIntent = "save" | "release" | "receive";

const TransmittalForm = () => {
  const { id } = useParams({ strict: false });
  const { action } = useRouteHandle();
  const navigate = useNavigate();
  const formRef = useFormRef();
  const queryClient = useQueryClient();
  const [{ currentUser }] = useAuthContext();

  const readOnly = action === "display";
  const isCreate = !id;

  // Bug 157534 fix: Save/Release/Receive now all funnel through this one
  // onSubmit via formRef.current.submit() (see triggerSubmit below), so RHF's
  // real submit pipeline runs and errorRoot can map a thrown BadRequestError's
  // field errors onto the exact transmittalDocuments[n].xxx input via
  // setError, instead of us hand-building a single toast string. This ref
  // tells the shared onSubmit which endpoint/action the click intended.
  const submitIntentRef = useRef<SubmitIntent>("save");

  const { data: record, isLoading: loading } = useQuery({
    queryKey: ["transmittal-record", id],
    queryFn: () => getTransmittalRecordById(id!),
    enabled: Boolean(id),
  });

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const isIssuer =
    isCreate ||
    Boolean(
      myDetails?.id && record?.issuerId && myDetails.id === record.issuerId,
    );

  const { data: coordinatorsData } = useQuery({
    queryKey: ["coordinators"],
    queryFn: async () => {
      const result = await getCoordinators({
        payload: { pageNumber: 1, pageSize: 500 },
      });
      return result.items ?? [];
    },
  });
  const coordinators = coordinatorsData ?? [];

  const initialValues = useMemo(
    () =>
      record
        ? mapRecordToFormValues(record, coordinators, currentUser?.displayName)
        : {
            ...defaultValues,
            date: today,
            issuer: currentUser?.displayName ?? "",
          },
    [record, coordinators, currentUser],
  );

  const submit = useCallback(
    async (values: TransmittalFormValues): Promise<TransmittalFormValues> => {
      if (!id) {
        notification.error("Cannot save: missing transmittal record id.");
        throw new Error("Missing transmittal record id");
      }

      const intent = submitIntentRef.current;
      const payload = buildInputPayload(values, record, coordinators);

      try {
        if (intent === "release") {
          await releaseTransmittalRecord(id, { id, payload });
          await queryClient.invalidateQueries({
            queryKey: ["transmittal-record", id],
          });
          await queryClient.invalidateQueries({
            queryKey: ["transmittal-records"],
          });
          notification.success("Transmittal request released.");
          return values;
        }

        if (intent === "receive") {
          await receiveTransmittalRecord(id, { id, payload });
          await queryClient.invalidateQueries({
            queryKey: ["transmittal-record", id],
          });
          await queryClient.invalidateQueries({
            queryKey: ["transmittal-records"],
          });
          notification.success("Transmittal request received.");
          return values;
        }

        // "save"
        await saveTransmittalRecord(id, { id, payload });
        await queryClient.invalidateQueries({
          queryKey: ["transmittal-record", id],
        });
        await queryClient.invalidateQueries({
          queryKey: ["transmittal-records"],
        });
        notification.success("Draft saved successfully.");
        navigate({
          to: "/document-transmittal/$id/display",
          params: { id },
        });
        return values;
      } catch (e) {
        console.error(e);
        const isValidationError = e instanceof BadRequestError;
        notification.error(
          isValidationError
            ? "One or more validation errors occurred. Please review the highlighted fields."
            : intent === "release"
              ? "Unable to release transmittal request. Please try again."
              : intent === "receive"
                ? "Unable to receive transmittal request. Please try again."
                : "Unable to save draft. Please try again.",
        );
        throw e;
      }
    },
    [id, record, coordinators, navigate, queryClient],
  );

  const triggerSubmit = useCallback(
    (intent: SubmitIntent) => {
      submitIntentRef.current = intent;
      formRef.current?.submit();
    },
    [formRef],
  );

  const onDiscard = useCallback(() => {
    if (!formRef.current) return;

    const proceedDiscard = () => navigate({ to: "/document-transmittal" });

    if (formRef.current.formState.isDirty) {
      dialogBox.confirm({
        title: "Discard changes?",
        content: (
          <>Are you sure you want to discard this transmittal request?</>
        ),
        actions: {
          confirm: { text: "Yes, discard", onClick: proceedDiscard },
          cancel: { text: "No" },
        },
      });
    } else {
      proceedDiscard();
    }
  }, [navigate]);

  const onBack = useCallback(() => {
    if (!formRef.current) return;

    const proceedBack = () => navigate({ to: "/document-transmittal" });

    const hasUnsavedChanges =
      Object.keys(formRef.current.formState.dirtyFields).length > 0;

    if (hasUnsavedChanges) {
      dialogBox.confirm({
        title: "Leave without saving?",
        content: (
          <>
            Are you sure you want to go back? Any unsaved changes will be lost.
          </>
        ),
        actions: {
          confirm: { text: "Yes", onClick: proceedBack },
          cancel: { text: "No" },
        },
      });
    } else {
      proceedBack();
    }
  }, [navigate]);

  return (
    <ContentLayout>
      <ContentHeader loading={loading}>
        <ContentHeaderBackNavigation buttonProps={{ onClick: onBack }} />
        <ContentHeaderTitle
          title={`${capitalCase(id ? action || "display" : "create")} Transmittal Request`}
        />
        <ContentHeaderBreadcrumbs />
      </ContentHeader>
      <Form
        ref={formRef}
        onSubmit={submit}
        values={initialValues}
        defaultValues={defaultValues}
        errorRoot="payload"
      >
        {({ control, setValue, getValues }) => {
          const currentStatus = useWatch({ control, name: "status" });
          const statusValue = currentStatus?.value ?? "Draft";

          const isDraft = statusValue === "Draft";
          const isReleased = statusValue === "Released";
          const isCurrentUserReceiver = Boolean(
            myDetails?.id && myDetails.id === record?.receiverId,
          );

          const canAddRow = isCreate || (!readOnly && isDraft);

          const handleRelease = () => {
            if (!id || !formRef.current) return;
            const releaseValues = formRef.current.getValues();
            if (
              releaseValues.receiverId &&
              releaseValues.receiverId === record?.issuerId
            ) {
              notification.error(
                "Issuer and Receiver cannot be the same. Please select a different Receiver.",
              );
              return;
            }
            dialogBox.confirm({
              title: "Release transmittal request",
              content: (
                <>Are you sure you want to release the transmittal request?</>
              ),
              actions: {
                confirm: {
                  text: "Yes",
                  onClick: () => triggerSubmit("release"),
                },
                cancel: { text: "No" },
              },
            });
          };

          const handleReceive = () => {
            if (!id || !formRef.current) return;
            dialogBox.confirm({
              title: "Receive transmittal request",
              content: (
                <>Are you sure you want to receive the transmittal request?</>
              ),
              actions: {
                confirm: {
                  text: "Yes",
                  onClick: () => triggerSubmit("receive"),
                },
                cancel: { text: "No" },
              },
            });
          };

          const handleCancel = () => {
            if (!id) return;
            dialogBox.confirm({
              title: "Cancel transmittal request",
              content: (
                <>Are you sure you want to cancel this transmittal request?</>
              ),
              actions: {
                confirm: {
                  text: "Yes",
                  onClick: async () => {
                    try {
                      await cancelTransmittalRecord(id);
                      await queryClient.invalidateQueries({
                        queryKey: ["transmittal-records"],
                      });
                      notification.success("Transmittal request cancelled.");
                      navigate({ to: "/document-transmittal" });
                    } catch (e) {
                      console.error(e);
                      notification.error(
                        getServerErrorMessage(
                          e,
                          "Unable to cancel transmittal request. Please try again.",
                        ),
                      );
                    }
                  },
                },
                cancel: { text: "No" },
              },
            });
          };

          const flatColumns: ControlledDataGridColumnProps<TransmittalDocumentRow>[] =
            [
              {
                field: "documentName",
                title: "Document Name",
                editor: "text",
                width: "250px",
                locked: true,
              },
              {
                field: "quantityReleased",
                title: "Quantity",
                editor: "numeric",
                width: "150px",
                editorProps: { min: 0, format: "n0" },
              },
              {
                field: "attachment",
                title: "Attachment",
                width: "400 px",
                editor: "upload",
                editorProps: ({
                  dataItem,
                }: {
                  dataItem: TransmittalDocumentRow;
                }) =>
                  buildTransmittalUploadEditorProps({
                    transmittalDocumentId: dataItem.id,
                    readOnly: false,
                  }),
              },
              {
                field: "remarkReleased",
                title: "Remarks",
                editor: "textarea",
              },
            ];

          const flatViewColumns: ControlledDataGridColumnProps<TransmittalDocumentRow>[] =
            [
              {
                field: "documentName",
                title: "Document Name",
                readOnly: true,
                width: "250px",
                locked: true,
              },
              {
                field: "quantityReleased",
                title: "Quantity Released",
                width: "150px",
                readOnly: true,
              },
              {
                field: "attachment",
                title: "Attachment Released",
                width: "400px",
                editor: "upload",
                editorProps: ({
                  dataItem,
                }: {
                  dataItem: TransmittalDocumentRow;
                }) =>
                  buildTransmittalUploadEditorProps({
                    transmittalDocumentId: dataItem.id,
                    readOnly: true,
                  }),
              },
              {
                field: "remarkReleased",
                title: "Remarks Released",
                readOnly: true,
                width: "400px",
              },
              {
                field: "quantityReceived",
                title: "Quantity Received",
                width: "150px",
                editor:
                  isReleased && isCurrentUserReceiver ? "numeric" : undefined,
                readOnly: !(isReleased && isCurrentUserReceiver),
                editorProps: ({
                  dataItem,
                }: {
                  dataItem: TransmittalDocumentRow;
                }) => ({
                  max: dataItem.quantityReleased ?? undefined,
                  min: 0,
                  format: "n0",
                }),
              },
              {
                field: "receiveAttachment",
                title: "Attachment Received",
                width: "400px",
                editor: "upload",
                editorProps: ({
                  dataItem,
                }: {
                  dataItem: TransmittalDocumentRow;
                }) =>
                  buildTransmittalUploadEditorProps({
                    transmittalDocumentId: dataItem.id,
                    readOnly: !(isReleased && isCurrentUserReceiver),
                  }),
              },
              {
                field: "remarkReceived",
                title: "Remarks Received",
                width: "400px",
                editor:
                  isReleased && isCurrentUserReceiver ? "textarea" : undefined,
                readOnly: !(isReleased && isCurrentUserReceiver),
              },
            ];

          return (
            <>
              <FormSection title="Header" loading={loading}>
                <FormFieldSet cols={2}>
                  {!isCreate && (
                    <>
                      <ControlledInput
                        name="transmittalId"
                        control={control}
                        loading={loading}
                        label="Transmittal ID"
                        readOnly
                      />
                      <ControlledComboBox
                        name="status"
                        control={control}
                        loading={loading}
                        label="Status"
                        data={STATUS_OPTIONS}
                        dataItemKey="value"
                        textField="label"
                        readOnly
                      />
                    </>
                  )}
                  <ControlledDatePicker
                    name="date"
                    control={control}
                    loading={loading}
                    label="Date"
                    readOnly
                  />
                  <ControlledInput
                    name="issuer"
                    control={control}
                    loading={loading}
                    label="Issuer"
                    readOnly
                  />
                  {coordinatorsData && (
                    <ControlledComboBox
                      name="receiverId"
                      control={control}
                      loading={loading}
                      label="Receiver"
                      data={coordinatorsData}
                      dataItemKey="id"
                      textField="fieldName"
                      readOnly={readOnly}
                      required
                    />
                  )}
                  {!isCreate && (
                    <>
                      <ControlledDatePicker
                        name="releasedDate"
                        control={control}
                        loading={loading}
                        label="Released"
                        format="MM/dd/yyyy HH:mm"
                        readOnly
                      />
                      <ControlledDatePicker
                        name="receivedDate"
                        control={control}
                        loading={loading}
                        label="Received"
                        format="MM/dd/yyyy HH:mm"
                        readOnly
                      />
                    </>
                  )}
                </FormFieldSet>
              </FormSection>

              {readOnly ? (
                <FormSection title="Document List" loading={loading}>
                  <ControlledDataGrid
                    fieldArrayName="transmittalDocuments"
                    columns={flatViewColumns}
                    canAdd={false}
                    hideRowCommandToggle
                    control={control}
                    dataItemKey="id"
                    loading={loading}
                  />
                </FormSection>
              ) : (
                <FormSection title="Document Details" loading={loading}>
                  <ControlledDataGrid
                    fieldArrayName="transmittalDocuments"
                    columns={flatColumns}
                    defaultAppend={defaultDocumentRow}
                    hideRowCommandToggle={!canAddRow}
                    readOnly={!canAddRow}
                    canAdd={canAddRow}
                    control={control}
                    dataItemKey="id"
                    loading={loading}
                    itemFactory={async (item) => {
                      if (!id) {
                        notification.error(
                          "Cannot add a document: missing transmittal record id.",
                        );
                        throw new Error("Missing transmittal record id");
                      }
                      const created = await createTransmittalDocument({
                        payload: { transmittalRecordId: id },
                      });
                      return { ...item, id: created.id! };
                    }}
                    onRemove={async (item) => {
                      try {
                        await deleteTransmittalDocument(item.id);
                        await queryClient.invalidateQueries({
                          queryKey: ["transmittal-record", id],
                        });
                      } catch (e) {
                        console.error(e);
                        notification.error(
                          "Unable to delete document. Please try again.",
                        );
                        throw e;
                      }
                    }}
                  />
                </FormSection>
              )}

              <FormActionsBar loading={loading}>
                {readOnly ? (
                  <>
                    {isDraft && isIssuer && (
                      <Button
                        type="button"
                        svgIcon={pencilIcon}
                        themeColor="base"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate({
                            to: "/document-transmittal/$id/edit",
                            params: { id: id! },
                          });
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    {isReleased && isCurrentUserReceiver && (
                      <Button
                        type="button"
                        themeColor="base"
                        onClick={handleReceive}
                      >
                        Receive
                      </Button>
                    )}
                    {isDraft && isIssuer && (
                      <Button
                        type="button"
                        themeColor="base"
                        onClick={handleRelease}
                      >
                        Release
                      </Button>
                    )}
                    {isDraft && isIssuer && (
                      <Button
                        type="button"
                        themeColor="error"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      svgIcon={saveIcon}
                      themeColor="primary"
                      onClick={() => triggerSubmit("save")}
                    >
                      Save
                    </Button>
                    {isCreate && (
                      <Button
                        type="button"
                        fillMode="outline"
                        svgIcon={cancelIcon}
                        themeColor="secondary"
                        onClick={onDiscard}
                      >
                        Cancel
                      </Button>
                    )}
                  </>
                )}
                <Button type="button" fillMode="outline" onClick={onBack}>
                  Back
                </Button>
              </FormActionsBar>
            </>
          );
        }}
      </Form>
    </ContentLayout>
  );
};

export default TransmittalForm;
